/**
 * Copilot tracking middleware
 * ═══════════════════════════════════════════════════════════════
 *
 * The provider can only observe navigation. Everything else — a save
 * succeeding, a save failing, a search returning nothing — is known to the
 * screen, and wiring 19 actions screen by screen is a lot of edits that will
 * drift.
 *
 * RTK Query already sees every mutation and query in the app. Hooking here
 * covers five of the highest-value actions in one place, with no per-screen
 * work and nothing to keep in sync:
 *
 *     save_success · save_error · error_shown · search_run · search_empty
 *
 * The remaining ones — form_abandon, field_edit, undo, help_open — genuinely
 * need the screen, because only it knows the intent. `useCopilotTracking`
 * makes those one line each.
 *
 * This never throws into the reducer chain: a tracking failure must not break
 * a dispatch.
 */

/** Endpoint names that are searches rather than ordinary reads. */
const SEARCH_ENDPOINTS = /search|query|filter/i;

/**
 * Endpoint naming in this codebase is consistent enough to infer intent:
 * 80 `create*`, 54 `delete*`, 3 `duplicate*`, 268 `get*`, 104 `list*`.
 *
 * Inferring here means four record actions need no per-screen wiring at all.
 * A screen only has to report what the API cannot see — that a form was
 * abandoned rather than submitted, or that someone asked for help.
 */
const RECORD_ACTION = [
  [/^duplicate/i, 'record_duplicate'],
  [/^create/i, 'record_create'],
  [/^delete|^remove/i, 'record_delete'],
];

/** A single-record read. `list*` is not opening a record. */
const isRecordOpen = (endpoint, arg) =>
  /^get/i.test(endpoint) && !/^getAll|^getList/i.test(endpoint)
  && (typeof arg === 'string' || (arg && typeof arg === 'object' && Object.keys(arg).some((k) => /id$/i.test(k))));

/** A 4xx carrying field-level errors is a validation failure, not a crash. */
const isValidationFailure = (payload) => {
  const status = payload?.status;
  if (status !== 400 && status !== 422) return false;
  const d = payload?.data?.error || payload?.data || {};
  return !!(d.details || d.errors || d.fields || /valid/i.test(d.code || ''));
};

/** Set by CopilotProvider once a copilot is bound and recording is permitted. */
let emit = null;

/** Last arguments per list endpoint, so a re-fetch can be told from a filter change. */
const lastListArgs = new Map();

/**
 * Endpoints that failed recently. A request to one of these is a retry —
 * which is observable here and needs no button to hang off. There is no
 * user-facing "retry" control in this codebase, so inferring it is the only
 * way the action exists at all.
 */
const recentFailures = new Map();
const RETRY_WINDOW_MS = 60000;

export function setCopilotEmitter(fn) {
  emit = fn;
  // Forget list arguments when the copilot changes, so the first fetch under a
  // new copilot is not mistaken for a filter change.
  if (!fn) { lastListArgs.clear(); recentFailures.clear(); }
}

/**
 * Redux middleware is `store => next => action`, three levels.
 *
 * This was written as `() => next => action` and registered as
 * `concat(copilotTrackingMiddleware())` — so the value handed to Redux was
 * already `next => action`, and Redux passed the store in as `next`. The first
 * dispatch then threw `next is not a function`.
 *
 * Registered without calling it: `concat(copilotTrackingMiddleware)`.
 */
export const copilotTrackingMiddleware = (store) => (next) => (action) => {
  const result = next(action);
  if (!emit) return result;

  try {
    const type = action?.type || '';
    if (!type.startsWith('api/executeMutation') && !type.startsWith('api/executeQuery')) return result;

    const endpoint = action?.meta?.arg?.endpointName || '';
    const isMutation = type.startsWith('api/executeMutation');

    if (type.endsWith('/rejected')) {
      // A validation failure is a different signal from a crash — it usually
      // means the person needs help with the form, which is exactly when a
      // copilot is useful.
      if (isValidationFailure(action?.payload)) {
        emit('validation_fail', { endpoint, status: action?.payload?.status ?? null });
        return result;
      }
      emit(isMutation ? 'save_error' : 'error_shown', {
        endpoint,
        status: action?.payload?.status ?? null,
      });
      recentFailures.set(endpoint, Date.now());
      return result;
    }

    if (type.endsWith('/fulfilled')) {
      // Succeeding on an endpoint that failed moments ago is a retry that
      // worked — worth a copilot knowing, because the person just struggled.
      const failedAt = recentFailures.get(endpoint);
      if (failedAt && Date.now() - failedAt < RETRY_WINDOW_MS) {
        emit('retry', { endpoint, afterMs: Date.now() - failedAt });
        recentFailures.delete(endpoint);
      }

      if (isMutation) {
        emit('save_success', { endpoint });
        // …and what KIND of change it was, inferred from the endpoint name.
        const kind = RECORD_ACTION.find(([re]) => re.test(endpoint))?.[1];
        if (kind) emit(kind, { endpoint });
      } else if (isRecordOpen(endpoint, action?.meta?.arg?.originalArgs)) {
        emit('record_open', { endpoint });
      } else if (SEARCH_ENDPOINTS.test(endpoint)) {
        const data = action?.payload?.data ?? action?.payload;
        const rows = Array.isArray(data) ? data
          : (data?.results || data?.items || data?.rows || null);
        emit('search_run', { endpoint, count: Array.isArray(rows) ? rows.length : null });
        if (Array.isArray(rows) && rows.length === 0) emit('search_empty', { endpoint });
      } else if (/^list/i.test(endpoint)) {
        // A list re-fetched with different arguments is a filter change. The
        // first fetch of a route is not — that is already page_view.
        const args = action?.meta?.arg?.originalArgs;
        const key = `${endpoint}:${JSON.stringify(args ?? null)}`;
        if (lastListArgs.has(endpoint) && lastListArgs.get(endpoint) !== key) {
          emit('filter_change', { endpoint });
        }
        lastListArgs.set(endpoint, key);
      }
    }
  } catch {
    // Tracking must never break a dispatch.
  }
  return result;
};
